import { useEffect, useState } from 'react';
import {
  Container,
  Stack,
  Title,
  Text,
  Badge,
  Group,
  Loader,
  Center,
  Card,
  Button,
} from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import { Link } from 'react-router';
import { useAuthController } from '../useAuthController';
import { usePresence } from '../usePresence';
import { useUserRepository } from '../rpc';
import { getFileUrls } from '@ayocore/platform/filemanager/client/rpc';
import { UserAvatar } from './UserAvatar';

/**
 * Форматирует дату последнего входа:
 * - Сегодня → "чч:мм"
 * - Вчера → "Вчера чч:мм"
 * - Иначе → "дд.мм.гггг чч:мм"
 */
function formatLastLogin(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateDay.getTime() === today.getTime()) return time;
  if (dateDay.getTime() === yesterday.getTime()) return `Вчера ${time}`;
  return (
    date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    time
  );
}

/** Данные профиля, загружаемые для отображения */
interface ProfileData {
  id: number;
  name: string;
  username: string | null;
  email: string | null;
  roles: string[];
  lastLoginAt: string | null;
  isActive: boolean;
  avatarThumbUrl: string | null;
  avatarMediumUrl: string | null;
}

interface UserProfileViewProps extends Record<string, unknown> {
  /** Username для загрузки профиля (приоритет над userId) */
  username?: string;
  /** ID пользователя — используется если username не задан */
  userId?: number;
  /** Режим отображения в дравере/модалке */
  isModal?: boolean;
}

/**
 * UserProfileView — компонент просмотра профиля пользователя.
 *
 * Работает как самостоятельная страница и в дравере (isModal).
 * Поддерживает загрузку по username или userId.
 */
export function UserProfileView({ username, userId, isModal = false }: UserProfileViewProps) {
  const { user: currentUser } = useAuthController(s => ({ user: s.user }));
  const { isUserOnline, getUserStatus } = usePresence();
  const userRepo = useUserRepository();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isOwnProfile = !!(
    (username && currentUser?.username === username) ||
    (userId && currentUser?.id === userId)
  );

  useEffect(() => {
    if (!username && !userId) return;
    void loadProfile();
  }, [username, userId]);

  async function loadProfile() {
    setLoading(true);
    setError(null);

    try {
      const user = username
        ? await userRepo.findByUsername(username)
        : await userRepo.findById(userId!);

      if (!user) {
        setError('Пользователь не найден');
        setLoading(false);
        return;
      }

      const roles = await userRepo.getUserRoles(user.id);

      const avatarUuids = [user.avatar_thumb_file_uuid, user.avatar_medium_file_uuid].filter(
        Boolean
      ) as string[];
      const avatarUrls = avatarUuids.length > 0 ? await getFileUrls(avatarUuids) : {};
      const avatarThumbUrl = user.avatar_thumb_file_uuid
        ? (avatarUrls[user.avatar_thumb_file_uuid] ?? null)
        : null;
      const avatarMediumUrl = user.avatar_medium_file_uuid
        ? (avatarUrls[user.avatar_medium_file_uuid] ?? null)
        : null;

      setProfile({
        id: user.id,
        name: user.name,
        username: user.username ?? null,
        email: user.email ?? null,
        roles,
        lastLoginAt: user.last_login_at ? String(user.last_login_at) : null,
        isActive: user.is_active,
        avatarThumbUrl,
        avatarMediumUrl,
      });
    } catch {
      setError('Не удалось загрузить профиль');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (error || !profile) {
    return (
      <Container size='sm' py='xl'>
        <Text c='red'>{error ?? 'Пользователь не найден'}</Text>
      </Container>
    );
  }

  const content = (
    <Stack align='center' gap='md'>
      <UserAvatar
        avatarThumbUrl={profile.avatarThumbUrl}
        avatarMediumUrl={profile.avatarMediumUrl}
        name={profile.name}
        size='lg'
      />

      <Stack align='center' gap={4}>
        <Title order={isModal ? 4 : 3}>{profile.name}</Title>
        {profile.username && (
          <Text size='sm' c='dimmed'>
            @{profile.username}
          </Text>
        )}
      </Stack>

      {/* Роли */}
      {profile.roles.length > 0 && (
        <Group gap='xs'>
          {profile.roles.map(role => (
            <Badge key={role} variant='light' color='blue'>
              {role}
            </Badge>
          ))}
        </Group>
      )}

      {/* Информация */}
      <Stack gap='xs' w='100%'>
        {profile.email && (
          <Group justify='space-between'>
            <Text size='sm' c='dimmed'>
              Email
            </Text>
            <Text size='sm'>{profile.email}</Text>
          </Group>
        )}
        {profile.lastLoginAt && (
          <Group justify='space-between'>
            <Text size='sm' c='dimmed'>
              Последний вход
            </Text>
            <Text size='sm'>{formatLastLogin(profile.lastLoginAt)}</Text>
          </Group>
        )}
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            Статус
          </Text>
          <Text
            size='sm'
            fw={500}
            c={
              isUserOnline(profile.id)
                ? getUserStatus(profile.id) === 'away'
                  ? 'yellow'
                  : 'green'
                : 'gray'
            }
          >
            {isUserOnline(profile.id)
              ? getUserStatus(profile.id) === 'away'
                ? 'Отошёл'
                : 'В сети'
              : 'Не в сети'}
          </Text>
        </Group>
      </Stack>

      {/* Кнопка редактирования для своего профиля */}
      {isOwnProfile && (
        <Button
          component={Link}
          to='/identity'
          variant='light'
          leftSection={<IconEdit size={16} />}
        >
          Редактировать
        </Button>
      )}
    </Stack>
  );

  if (isModal) {
    return content;
  }

  return (
    <Container size='sm' py='xl'>
      <Card shadow='sm' padding='lg' radius='md' withBorder>
        {content}
      </Card>
    </Container>
  );
}
